import { Controller } from "@hotwired/stimulus";

declare global {
  interface Window {
    jQuery?: any;
  }
}

type WdayOption = [number, string];
type InstructorOption = [number, string];
type LocationOption = [number, string];

interface Row {
  index: number;
  title: string;
  wdays: number[];
  duration: string;
  start_date: string | null;
  start_time: string | null;
  primary_instructor_id: number | null;
  kind: string;
  video_conference_account_id?: number | null;
  location_id: number | null;
  address?: string;
  lat?: number | null;
  lng?: number | null;
}

interface PreviewRow extends Row {
  valid: boolean;
  errors: Record<string, string[]>;
  meeting_count: number;
  computed_end_date: string | null;
}

export default class extends Controller {
  static targets = [
    "step1",
    "step2",
    "step3",
    "count",
    "rowsBody",
    "reviewBody",
  ];
  static values = {
    templateId: Number,
    rowsUrl: String,
    previewUrl: String,
    generateUrl: String,
    wdayOptions: Array,
    wdaysPlaceholder: String,
    instructorOptions: Array,
    instructorPlaceholder: String,
    locationOptions: Array,
    locationPlaceholder: String,
    i18n: Object,
  };

  declare step1Target: HTMLElement;
  declare step2Target: HTMLElement;
  declare step3Target: HTMLElement;
  declare countTarget: HTMLInputElement;
  declare rowsBodyTarget: HTMLElement;
  declare reviewBodyTarget: HTMLElement;

  declare templateIdValue: number;
  declare rowsUrlValue: string;
  declare previewUrlValue: string;
  declare generateUrlValue: string;
  declare wdayOptionsValue: WdayOption[];
  declare wdaysPlaceholderValue: string;
  declare instructorOptionsValue: InstructorOption[];
  declare instructorPlaceholderValue: string;
  declare locationOptionsValue: LocationOption[];
  declare locationPlaceholderValue: string;
  declare i18nValue: Record<string, string>;

  t(key: string): string {
    return this.i18nValue?.[key] ?? key;
  }

  rows: Row[] = [];

  connect(): void {
    this.rows = [];
  }

  showStep(n: number): void {
    [this.step1Target, this.step2Target, this.step3Target].forEach((el, i) => {
      el.hidden = i + 1 !== n;
    });
  }

  async loadRows(): Promise<void> {
    const count = parseInt(this.countTarget.value, 10);
    const r = await this.post<{ rows: Row[] }>(this.rowsUrlValue, { count });
    this.rows = r.rows;
    this.step2Target.innerHTML = this.buildStep2Html();
    this.initWdaySelects();
    this.initInstructorSelects();
    this.initLocationSelects();
    this.showStep(2);
  }

  buildWdayOptionsHtml(selected: number[]): string {
    const set = new Set(selected.map((n) => Number(n)));
    return this.wdayOptionsValue
      .map(
        ([value, label]) =>
          `<option value="${value}"${set.has(Number(value)) ? " selected" : ""}>${this.esc(label)}</option>`
      )
      .join("");
  }

  initWdaySelects(): void {
    const jq = window.jQuery;
    if (!jq) return;
    const selects = this.step2Target.querySelectorAll<HTMLSelectElement>(
      'select[name="wdays"]'
    );
    selects.forEach((el) => {
      const $el = jq(el);
      if (typeof $el.select2 !== "function") return;
      $el.select2({
        width: "100%",
        placeholder: this.wdaysPlaceholderValue,
        allowClear: false,
      });
    });
  }

  initInstructorSelects(): void {
    const jq = window.jQuery;
    if (!jq) return;
    const selects = this.step2Target.querySelectorAll<HTMLSelectElement>(
      'select[name="primary_instructor_id"]'
    );
    selects.forEach((el) => {
      const $el = jq(el);
      if (typeof $el.select2 !== "function") return;
      $el.select2({
        width: "100%",
        placeholder: this.instructorPlaceholderValue,
        allowClear: true,
      });
    });
  }

  buildInstructorOptionsHtml(selectedId: number | null): string {
    const opts = this.instructorOptionsValue
      .map(
        ([id, label]) =>
          `<option value="${id}"${selectedId === id ? " selected" : ""}>${this.esc(label)}</option>`
      )
      .join("");
    return `<option value=""></option>${opts}`;
  }

  initLocationSelects(): void {
    const jq = window.jQuery;
    if (!jq) return;
    const selects = this.step2Target.querySelectorAll<HTMLSelectElement>(
      'select[name="location_id"]'
    );
    selects.forEach((el) => {
      const $el = jq(el);
      if (typeof $el.select2 !== "function") return;
      $el.select2({
        width: "100%",
        placeholder: this.locationPlaceholderValue,
        allowClear: true,
      });
    });
  }

  buildLocationOptionsHtml(selectedId: number | null): string {
    const opts = this.locationOptionsValue
      .map(
        ([id, label]) =>
          `<option value="${id}"${selectedId === id ? " selected" : ""}>${this.esc(label)}</option>`
      )
      .join("");
    return `<option value=""></option>${opts}`;
  }

  buildStep2Html(): string {
    const rows = this.rows
      .map(
        (row, i) => `
      <tr data-row-index="${i}" data-kind="${this.esc(row.kind || "")}">
        <td class="align-middle">${i + 1}</td>
        <td><input name="title" class="form-control form-control-sm" value="${this.esc(row.title)}"></td>
        <td style="min-width: 220px;">
          <select name="wdays" class="form-control form-control-sm" multiple>
            ${this.buildWdayOptionsHtml(row.wdays || [])}
          </select>
        </td>
        <td><input name="duration" class="form-control form-control-sm" value="${this.esc(row.duration || "")}"></td>
        <td><input type="date" name="start_date" class="form-control form-control-sm"></td>
        <td><input type="time" name="start_time" class="form-control form-control-sm" value="${this.esc(row.start_time || "")}"></td>
        <td style="min-width: 220px;">
          <select name="primary_instructor_id" class="form-control form-control-sm">
            ${this.buildInstructorOptionsHtml(row.primary_instructor_id)}
          </select>
        </td>
        <td style="min-width: 220px;">
          <select name="location_id" class="form-control form-control-sm">
            ${this.buildLocationOptionsHtml(row.location_id)}
          </select>
        </td>
      </tr>
    `
      )
      .join("");
    return `
      <div class="card mb-3">
        <div class="card-header">
          <h3 class="card-title mb-0">${this.esc(this.t("step2_title"))}</h3>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-sm border-bottom mb-0">
              <thead class="thead-light">
                <tr>
                  <th>${this.esc(this.t("row_number"))}</th>
                  <th>${this.esc(this.t("title"))}</th>
                  <th>${this.esc(this.t("wdays"))}</th>
                  <th>${this.esc(this.t("duration"))}</th>
                  <th>${this.esc(this.t("start_date"))}</th>
                  <th>${this.esc(this.t("start_time"))}</th>
                  <th>${this.esc(this.t("instructor"))}</th>
                  <th>${this.esc(this.t("location"))}</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
        </div>
        <div class="card-footer">
          <button type="button" class="btn btn-primary" data-action="group-generator#preview">
            ${this.esc(this.t("next"))} <i class="fas fa-arrow-right ml-1"></i>
          </button>
        </div>
      </div>
    `;
  }

  collectRows(): Row[] {
    const tbody = this.step2Target.querySelector("tbody");
    if (!tbody) return [];
    return [...tbody.querySelectorAll("tr")].map((tr, i) => {
      const get = (name: string): string =>
        (tr.querySelector(`[name="${name}"]`) as HTMLInputElement)?.value || "";
      const wdaysSelect = tr.querySelector<HTMLSelectElement>(
        'select[name="wdays"]'
      );
      const wdays = wdaysSelect
        ? [...wdaysSelect.selectedOptions]
            .map((opt) => parseInt(opt.value, 10))
            .filter((n) => Number.isFinite(n))
        : [];
      return {
        index: i,
        title: get("title"),
        wdays,
        duration: get("duration"),
        start_date: get("start_date") || null,
        start_time: get("start_time") || null,
        primary_instructor_id:
          parseInt(get("primary_instructor_id"), 10) || null,
        location_id: parseInt(get("location_id"), 10) || null,
        kind: tr.dataset.kind || "online",
      };
    });
  }

  async preview(): Promise<void> {
    const rows = this.collectRows();
    const r = await this.post<{ rows: PreviewRow[] }>(this.previewUrlValue, {
      rows,
    });
    const invalid = r.rows.filter((x) => !x.valid).length;
    const totalMeetings = r.rows.reduce((a, x) => a + x.meeting_count, 0);
    this.step3Target.innerHTML = this.buildStep3Html(r.rows, invalid, totalMeetings);
    this.showStep(3);
  }

  back(): void {
    this.showStep(2);
  }

  formatRowErrors(errors: Record<string, string[]>): string {
    const messages = Object.entries(errors).flatMap(([field, msgs]) =>
      msgs.map((m) => (field === "base" ? m : `${field}: ${m}`))
    );
    if (messages.length === 0) return "";
    return `<ul class="mb-0 small text-danger">${messages
      .map((m) => `<li>${this.esc(m)}</li>`)
      .join("")}</ul>`;
  }

  buildStep3Html(rows: PreviewRow[], invalid: number, totalMeetings: number): string {
    const rowsHtml = rows
      .map(
        (row, i) => `
      <tr class="${row.valid ? "" : "table-warning"}">
        <td>${i + 1}</td>
        <td>
          ${this.esc(row.title)}
          ${row.valid ? "" : this.formatRowErrors(row.errors || {})}
        </td>
        <td>${row.start_date || ""}</td>
        <td>${row.computed_end_date || "—"}</td>
        <td>${row.meeting_count}</td>
      </tr>
    `
      )
      .join("");
    const invalidMsg = this.t("invalid_rows").replace("%{count}", String(invalid));
    const willCreateMsg = this.t("will_create")
      .replace("%{groups}", String(rows.length))
      .replace("%{meetings}", String(totalMeetings));
    const banner =
      invalid > 0
        ? `<div class="alert alert-warning mb-3">
             <i class="fas fa-exclamation-triangle mr-1"></i>
             ${this.esc(invalidMsg)}
           </div>`
        : `<div class="alert alert-info mb-3">
             ${this.esc(willCreateMsg)}
           </div>`;
    const generateBtn =
      invalid > 0
        ? `<button type="button" class="btn btn-primary" disabled>
             <i class="fas fa-magic mr-1"></i> ${this.esc(this.t("generate"))}
           </button>`
        : `<button type="button" class="btn btn-primary" data-action="group-generator#generate">
             <i class="fas fa-magic mr-1"></i> ${this.esc(this.t("generate"))}
           </button>`;
    return `
      <div class="card mb-3">
        <div class="card-header">
          <h3 class="card-title mb-0">${this.esc(this.t("step3_title"))}</h3>
        </div>
        <div class="card-body">
          ${banner}
          <div class="table-responsive">
            <table class="table table-sm border-bottom mb-0">
              <thead class="thead-light">
                <tr>
                  <th>${this.esc(this.t("row_number"))}</th>
                  <th>${this.esc(this.t("title"))}</th>
                  <th>${this.esc(this.t("start_date"))}</th>
                  <th>${this.esc(this.t("end_date"))}</th>
                  <th>${this.esc(this.t("meeting_count"))}</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>
        </div>
        <div class="card-footer d-flex justify-content-between">
          <button type="button" class="btn btn-falcon-default" data-action="group-generator#back">
            <i class="fas fa-arrow-left mr-1"></i> ${this.esc(this.t("back"))}
          </button>
          ${generateBtn}
        </div>
      </div>
    `;
  }

  async generate(): Promise<void> {
    const rows = this.collectRows();
    const form = document.createElement("form");
    form.method = "POST";
    form.action = this.generateUrlValue;
    const csrfMeta = document.querySelector<HTMLMetaElement>(
      "meta[name=csrf-token]"
    );
    form.innerHTML = `
      <input name="authenticity_token" value="${csrfMeta?.content || ""}">
      <input name="rows" value='${JSON.stringify(rows).replace(/'/g, "&#39;")}'>
    `;
    document.body.appendChild(form);
    form.submit();
  }

  async post<T>(url: string, body: unknown): Promise<T> {
    const csrfMeta = document.querySelector<HTMLMetaElement>(
      "meta[name=csrf-token]"
    );
    const csrf = csrfMeta?.content || "";
    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    return resp.json() as Promise<T>;
  }

  esc(str: string): string {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}
